import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, NotebookText, X } from "lucide-react";
import { SlidesContentDTO } from "@superlekcja/shared";
import { SlideContent } from "./slide-content";
import { useUiV2 } from "@/ui-v2/use-ui-v2";
import { cn } from "@/lib/utils";

export function SlidesPresenter({ deck, onClose, titleImageUrl }: { deck: SlidesContentDTO; onClose: () => void; titleImageUrl?: string }) {
    const { t } = useTranslation();
    const uiV2 = useUiV2();
    const [index, setIndex] = useState(0);
    // Telefon w poziomie ma ~390 px wysokosci: panel notatek zabieralby jedna trzecia ekranu,
    // a slajd jest tym, co widzi klasa. Notatki wlacza sie tam przyciskiem.
    const [notesVisible, setNotesVisible] = useState(() => window.innerHeight >= 500);
    const containerRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef<number | null>(null);

    const total = deck.slides.length;
    const clamp = (i: number) => Math.max(0, Math.min(total - 1, i));
    const goTo = (i: number) => setIndex(clamp(i));

    useEffect(() => {
        containerRef.current?.requestFullscreen().catch(() => {});

        const onFullscreenChange = () => {
            if (!document.fullscreenElement) onClose();
        };
        document.addEventListener("fullscreenchange", onFullscreenChange);

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight" || e.key === " ") {
                e.preventDefault();
                setIndex((i) => clamp(i + 1));
            } else if (e.key === "ArrowLeft") {
                setIndex((i) => clamp(i - 1));
            } else if (e.key === "Escape") {
                if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
                onClose();
            } else if (e.key === "n") {
                setNotesVisible((v) => !v);
            } else if (e.key === "f") {
                if (document.fullscreenElement) {
                    document.exitFullscreen().catch(() => {});
                } else {
                    containerRef.current?.requestFullscreen().catch(() => {});
                }
            }
        };
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("fullscreenchange", onFullscreenChange);
            window.removeEventListener("keydown", onKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const slide = deck.slides[index];

    // Chrome prezentera jest ciemne w obu wersjach — na rzutniku wszystko poza
    // slajdem ma zniknac. Redesign zmienia tylko odcien na cieply, zgodny
    // z paleta („Pastel"), zamiast zimnego granatu z v1.
    const chrome = uiV2 ? "bg-[#1E1830]" : "bg-[#1A1D33]";

    return createPortal(
        <div
            ref={containerRef}
            data-testid="slides-presenter"
            className={cn("fixed inset-0 z-50 flex flex-col portrait:max-sm:justify-center", chrome)}
        >
            <button
                onClick={onClose}
                aria-label={t('presenter-close')}
                className="absolute right-4 top-4 z-10 text-white/60 hover:text-white"
            >
                <X className="h-6 w-6" />
            </button>

            {/* Scena ma `container-type: size`, wiec slajd 16:9 liczy sie od miejsca, ktore NAPRAWDE
                zostalo po pasku notatek — dawne `90vh` ignorowalo pasek i w poziomie na telefonie
                slajd wylewal sie poza ekran. W pionie scena nie rosnie: slajd i notatki tworza jedna
                wycentrowana kolumne zamiast slajdu zawieszonego miedzy dwoma ciemnymi pasami. */}
            <div
                className="flex min-h-0 flex-1 items-center justify-center p-3 sm:p-8 [@media(max-height:500px)]:p-3 portrait:max-sm:aspect-video portrait:max-sm:flex-none"
                style={{ containerType: "size" }}
                onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                    if (touchStartX.current === null) return;
                    const dx = e.changedTouches[0].clientX - touchStartX.current;
                    touchStartX.current = null;
                    if (Math.abs(dx) > 50) goTo(index + (dx < 0 ? 1 : -1));
                }}
            >
                <div
                    className={cn("relative @container", uiV2 && "overflow-hidden rounded-xl shadow-2xl")}
                    style={{ width: "min(100cqw, 100cqh * 16 / 9)" }}
                >
                    <SlideContent slide={slide} titleImageUrl={titleImageUrl} />
                </div>
            </div>

            {/* Strzalki po bokach tylko tam, gdzie slajd zostawia boczne pasy — w pionie na
                telefonie lezalyby na slajdzie, wiec przechodza do paska na dole. */}
            <button
                onClick={() => goTo(index - 1)}
                disabled={index === 0}
                aria-label={t('presenter-previous-slide')}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white disabled:opacity-30 portrait:max-sm:hidden"
            >
                <ChevronLeft className="h-10 w-10" />
            </button>
            <button
                data-testid="presenter-next"
                onClick={() => goTo(index + 1)}
                disabled={index === total - 1}
                aria-label={t('presenter-next-slide')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white disabled:opacity-30 portrait:max-sm:hidden"
            >
                <ChevronRight className="h-10 w-10" />
            </button>

            {/* Pasek postepu talii: na rzutniku nauczyciel nie ma jak ocenic, ile
                lekcji zostalo, a licznik "7 / 24" trzeba przeczytac i przeliczyc. */}
            {uiV2 && (
                <div className="h-1 w-full bg-white/10" aria-hidden>
                    <div
                        className="h-full bg-[#FFC42E] transition-[width] duration-200"
                        style={{ width: `${((index + 1) / total) * 100}%` }}
                    />
                </div>
            )}

            <div className="flex flex-col gap-3 p-4 text-sm text-white sm:flex-row sm:items-start sm:gap-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => goTo(index - 1)}
                        disabled={index === 0}
                        aria-label={t('presenter-previous-slide')}
                        className="hidden rounded-full p-2 text-white/70 hover:text-white disabled:opacity-30 portrait:max-sm:block"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                    <div className="whitespace-nowrap font-semibold tabular-nums sm:pt-1">
                        {index + 1} / {total}
                    </div>
                    <button
                        data-testid="presenter-next"
                        onClick={() => goTo(index + 1)}
                        disabled={index === total - 1}
                        aria-label={t('presenter-next-slide')}
                        className="hidden rounded-full p-2 text-white/70 hover:text-white disabled:opacity-30 portrait:max-sm:block"
                    >
                        <ChevronRight className="h-6 w-6" />
                    </button>
                    {/* Na dotyku nie ma klawisza "n" — notatki przelacza przycisk. */}
                    <button
                        onClick={() => setNotesVisible((v) => !v)}
                        aria-pressed={notesVisible}
                        className="ml-auto flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs text-white/80 hover:text-white pointer-fine:hidden"
                    >
                        <NotebookText className="h-4 w-4" />
                        {t('presenter-notes-hint')}
                    </button>
                </div>
                {notesVisible && (
                    <div className={cn("h-24 overflow-y-auto rounded-lg p-3 sm:flex-1 portrait:max-sm:h-auto portrait:max-sm:max-h-[40vh]", uiV2 ? "bg-white/10" : "bg-primary/20")}>
                        <div className="text-xs uppercase tracking-wider text-white/60">{t('presenter-teacher-notes')}</div>
                        <div>{slide.speakerNotes || "—"}</div>
                    </div>
                )}
                <div className="hidden whitespace-nowrap pt-1 text-white/60 pointer-fine:block">
                    <kbd className="rounded bg-white/10 px-1.5 py-0.5">←</kbd>{" "}
                    <kbd className="rounded bg-white/10 px-1.5 py-0.5">→</kbd> {t('presenter-slides-hint')}{" "}
                    <kbd className="rounded bg-white/10 px-1.5 py-0.5">f</kbd> {t('presenter-fullscreen-hint')}{" "}
                    <kbd className="rounded bg-white/10 px-1.5 py-0.5">n</kbd> {t('presenter-notes-hint')}
                </div>
            </div>
        </div>,
        document.body
    );
}
